import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";

import ProjectUnitModal from "../Modals/ProjectUnitModal/ProjectUnitModal";
import { duplicateResponse } from "../Helpers/functions";
import GenericListContainer from "../GenericList/GenericListContainer";
import {
  FIND_PROJECT,
  GET_PROJECT_UNITS,
  GET_LOGIN_DATA,
  GET_ORGANIZATION,
  GET_COUNT_UNITS_BY_ORGANIZATION,
  GET_UNITS_BY_ORGANIZATION,
} from "../GraphQl/query";
import {
  ADD_OPERATIVE_UNIT,
  CREATE_ORGANIZATION,
  UPDATE_ORGANIZATON,
  UPDATE_PROJECT_UNIT,
} from "../GraphQl/mutation";
import { AddButton } from "../CommonComponents";
import {
  emptyProjectData,
  organizationFiscalCodeColumn,
  getModifyColumn,
  getManageUserColumn,
  getOrganizationDataToSave,
  getProjectUnitDataToSave,
} from "./functions";
import { OrganizationUpdateModal } from "../Modals/OrganizationModal/OrganizationUpdateModal";
import { PRJUNITS, USERS } from "../../navigation/Routes";
import { PageWrapper } from "../PageWrapper";
import { getDetailsColumn } from "../genericFunctions";

const ProjectUnitContainer = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [modalState, toggleModalState] = useState(false);
  const [updateOrganizationModal, toggleupdateOrganizationModal] =
    useState(false);
  const [values, setValues] = useState(false);
  const [fiscalCode, setFiscalCode] = useState();

  const { data: dataProject } = useQuery(FIND_PROJECT, {
    variables: { projectId: parseInt(projectId) },
  });

  const { data, loading, error } = useQuery(GET_PROJECT_UNITS, {
    variables: { projectId: parseInt(projectId) },
    fetchPolicy: "network-only",
  });

  const { data: userData } = useQuery(GET_LOGIN_DATA);

  const {
    loading: loadingOrganization,
    data: organizationData,
    error: errorOrganization,
  } = useQuery(GET_ORGANIZATION, {
    variables: { fiscalCode },
    skip: !fiscalCode,
  });

  const organizationId = organizationData?.result?.id;
  const isExternal = organizationData?.result?.isExternal;

  const { data: unitsByOrganizationCount } = useQuery(
    GET_COUNT_UNITS_BY_ORGANIZATION,
    {
      variables: { organizationId },
      skip: !isExternal,
    }
  );

  const { data: unitsByOrganization } = useQuery(GET_UNITS_BY_ORGANIZATION, {
    variables: {
      organizationId,
      managerId: userData?.userInfo?.id,
    },
    skip: !isExternal,
  });

  const redirectToUsersPage = (instituteCode) =>
    navigate(`${PRJUNITS}/${instituteCode}/${USERS}`);

  const [updateProjectUnit] = useMutation(UPDATE_PROJECT_UNIT);
  const [createProjectUnit] = useMutation(ADD_OPERATIVE_UNIT);
  const [createOrganization] = useMutation(CREATE_ORGANIZATION);
  const [updateOrganization] = useMutation(UPDATE_ORGANIZATON);

  const handleProjectUnitSubmit = (newValues, existingOrganization) => {
    const isUpdate = !!values?.id;
    const mutation = isUpdate ? updateProjectUnit : createProjectUnit;

    mutation({
      variables: {
        projectUnitId: values?.id,
        data: getProjectUnitDataToSave(
          newValues,
          projectId,
          existingOrganization
        ),
      },
      refetchQueries: [
        {
          query: GET_PROJECT_UNITS,
          variables: { projectId: parseInt(projectId) },
          awaitRefetchQueries: true,
        },
        {
          query: GET_UNITS_BY_ORGANIZATION,
          variables: { organizationId, managerId: userData?.userInfo?.id },
          awaitRefetchQueries: true,
        },
        {
          query: GET_COUNT_UNITS_BY_ORGANIZATION,
          variables: { organizationId },
          awaitRefetchQueries: true,
        },
      ],
    });
    toggleModalState(false);
  };

  const handleOrganizationSubmit = (newValues) => {
    const isUpdate = !!newValues?.organization?.id;
    const mutation = isUpdate ? updateOrganization : createOrganization;

    const variables = isUpdate
      ? {
          organizationId: newValues.organization.id,
          data: getOrganizationDataToSave(newValues, projectId),
        }
      : {
          data: getOrganizationDataToSave(newValues, projectId),
        };

    mutation({
      variables,
      update: (proxy, { data: { result } }) => {
        const data = proxy.readQuery({
          query: GET_ORGANIZATION,
          variables: { fiscalCode: fiscalCode },
        });
        proxy.writeQuery({
          query: GET_ORGANIZATION,
          variables: { fiscalCode: fiscalCode },
          data: {
            ...data,
            result: result,
          },
        });
      },
    });
    toggleupdateOrganizationModal(false);
  };

  const handleSubmit = (values, existingOrganization) => {
    existingOrganization
      ? handleProjectUnitSubmit(values, existingOrganization)
      : handleOrganizationSubmit(values);
  };

  const setProjectData = (aditionalData) => {
    setValues({
      ...aditionalData,
      project: dataProject?.result,
      projectId: dataProject?.result?.id,
    });
    setFiscalCode(aditionalData?.organization?.fiscalCode || null);
  };

  const openOrganizationUpdateModal = () => {
    toggleModalState(false);
    toggleupdateOrganizationModal(true);
  };

  const projectList = duplicateResponse(data?.result)?.map((project) => ({
    ...project,
    fiscalCode: project?.organization?.fiscalCode,
  }));

  const hasOrganization = projectList?.find(
    (project) => project?.organization?.fiscalCode === (fiscalCode || "").trim()
  );
  const hasProjectInfo =
    dataProject?.result?.code !== undefined &&
    dataProject?.result?.instituteDescription !== undefined;

  const missingHeader = hasProjectInfo
    ? dataProject.result.code + "\n" + dataProject.result.instituteDescription
    : "";

  const TableHeader = (
    <AddButton
      action={() => setProjectData(emptyProjectData)}
      toggleModalState={toggleModalState}
    />
  );

  const primaryColumns = [getDetailsColumn(setProjectData, toggleModalState)];

  const additionalColumns = [
    organizationFiscalCodeColumn,
    getManageUserColumn(redirectToUsersPage),
    getModifyColumn(setProjectData, toggleModalState),
  ];

  return (
    <PageWrapper error={error} loading={loading}>
      {modalState && (
        <ProjectUnitModal
          modalState={modalState}
          defaultValues={values}
          translate={t}
          hasOrganization={hasOrganization}
          organizationData={organizationData}
          errorOrganization={errorOrganization}
          loadingOrganization={loadingOrganization}
          unitsByOrganizationCount={unitsByOrganizationCount?.result}
          unitsByOrganization={unitsByOrganization?.result}
          setValues={setValues}
          toggleModal={toggleModalState}
          setFiscalCode={setFiscalCode}
          handleSubmit={handleSubmit}
          openOrganizationUpdateModal={openOrganizationUpdateModal}
        />
      )}
      {updateOrganizationModal && (
        <OrganizationUpdateModal
          modalState={updateOrganizationModal}
          toggleModal={toggleupdateOrganizationModal}
          translate={t}
          defaultValues={{
            organization: unitsByOrganization?.result?.[0].organization,
          }}
          handleSubmit={handleOrganizationSubmit}
        />
      )}
      <GenericListContainer
        list={projectList}
        structureName={"ProjectUnits"}
        missingheader={missingHeader}
        TableHeader={TableHeader}
        additionalColumns={additionalColumns}
        primaryColumns={primaryColumns}
      />
    </PageWrapper>
  );
};

export default ProjectUnitContainer;
